import React, { useState } from 'react'
import { Form } from 'react-bootstrap'
import { useNotificationService, useErrorService } from '@abroad/components'
import { PlayIcon } from '../assetsComponents'
import API from '../../utils/API'
import { openLinkInTab } from '../../utils/openLinkInTab'

const RSVP_SESSION_DESCRIPTION =
  'This session is for individuals looking to learn more about the Quest experience, meet others considering joining the trip and ask any questions to our Abroad team and Group Leaders.'

const RSVP_RECORDING_SESSION_DESCRIPTION =
  'Dive into the recording to explore the journey ahead, hear direct insights from our Abroad team and Group Leader(s), and get a feel for what awaits you on this extraordinary journey.'

const LEARN_MORE_TEXTAREA_MIN_HEIGHT = '80px'

function LearnMoreFieldError({ message }) {
  if (!message) return null
  return (
    <div
      className='learn-more-field-error'
      style={{
        fontSize: '0.875rem',
        color: '#dc3545',
        fontWeight: '500',
        marginTop: '8px',
        display: 'block',
        lineHeight: '1.2',
      }}>
      {message}
    </div>
  )
}

const LearnMoreSection = ({ questId, informationSession }) => {
  const Notification = useNotificationService()
  const Error = useErrorService()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    question: '',
  })
  const [errors, setErrors] = useState({})
  const [showValidation, setShowValidation] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formKey, setFormKey] = useState(0)

  const hasRecording = Boolean(informationSession?.recordingLink)
  const hasRsvpDetails =
    informationSession?.date &&
    informationSession?.time &&
    (informationSession?.rsvpLink || hasRecording)

  const validateForm = () => {
    const newErrors = {}
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    if (!formData.name.trim()) {
      newErrors.name = 'This field is required'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'This field is required'
    } else if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = 'Must be a valid email address'
    }
    if (!formData.question.trim()) {
      newErrors.question = 'This field is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setShowValidation(true)
    if (!validateForm()) return

    setIsSubmitting(true)
    try {
      await API.quests.postAskQuestion(questId, {
        name: formData.name.trim(),
        email: formData.email.trim(),
        question: formData.question.trim(),
      })
      setFormData({ name: '', email: '', question: '' })
      setErrors({})
      setShowValidation(false)
      setFormKey((key) => key + 1)
      Notification.showNotification(
        'Your question has been submitted!',
        'success',
      )
    } catch (err) {
      Error.showError(err)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section
      id='learn-more'
      className='mb-64px scroll-target learn-more-section'>
      <div className='learn-more-block'>
        <h2 className='display-5 font-heading sub-title text-rich-navy fw-medium'>
          Have A Question About This Experience?
        </h2>
        <div className='fw-semibold small font-paragraph small-paragraph-2 mb-5'>
          Send a note to our events team and we will respond shortly.
        </div>
        <div className='bg-white rounded-3 p-32px prosperity-card'>
          <Form
            key={formKey}
            onSubmit={handleSubmit}
            noValidate
            className='learn-more-form schedule-call-modal'>
            <div className='row'>
              <div className='col-md-6'>
                <Form.Group className='mb-3'>
                  <Form.Label className='quest-modal-lable'>
                    Full Name
                  </Form.Label>
                  <div className='linkedin-profile-input'>
                    <Form.Control
                      type='text'
                      name='name'
                      value={formData.name}
                      onChange={handleChange}
                      autoComplete='off'
                      className='learn-more-text-input'
                    />
                    {showValidation && (
                      <LearnMoreFieldError message={errors.name} />
                    )}
                  </div>
                </Form.Group>
              </div>
              <div className='col-md-6'>
                <Form.Group className='mb-3'>
                  <Form.Label className='quest-modal-lable'>Email</Form.Label>
                  <div className='linkedin-profile-input'>
                    <Form.Control
                      type='email'
                      name='email'
                      value={formData.email}
                      onChange={handleChange}
                      autoComplete='off'
                      className='learn-more-text-input'
                    />
                    {showValidation && (
                      <LearnMoreFieldError message={errors.email} />
                    )}
                  </div>
                </Form.Group>
              </div>
            </div>
            <Form.Group className='mb-3'>
              <div className='linkedin-profile-input'>
                <Form.Control
                  as='textarea'
                  name='question'
                  rows={1}
                  placeholder='Type your question here...'
                  value={formData.question}
                  onChange={handleChange}
                  className='auto-resize-textarea learn-more-textarea'
                  style={{
                    minHeight: LEARN_MORE_TEXTAREA_MIN_HEIGHT,
                    resize: 'none',
                    overflow: 'hidden',
                  }}
                  onInput={(e) => {
                    e.target.style.height = 'auto'
                    e.target.style.height = `${Math.max(
                      e.target.scrollHeight,
                      parseInt(LEARN_MORE_TEXTAREA_MIN_HEIGHT, 10),
                    )}px`
                  }}
                />
                {showValidation && (
                  <LearnMoreFieldError message={errors.question} />
                )}
              </div>
            </Form.Group>
            <div className='d-flex justify-content-end mt-2'>
              <button
                type='submit'
                className='btn btn-gold text-white text-uppercase prosperity-button-cta font-paragraph small-paragraph-bold px-5'
                disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </Form>
        </div>
      </div>

      {hasRsvpDetails && (
        <div className='learn-more-block'>
          <h2 className='display-5 font-heading sub-title mb-5 text-rich-navy fw-medium'>
            {hasRecording
              ? 'Missed The Live Experience Information'
              : 'RSVP To Our Live Experience Information Session'}
          </h2>
          <div className='bg-white rounded-3 p-32px prosperity-card'>
            <div className='learn-more-rsvp-details'>
              <div className='learn-more-rsvp-item'>
                <span className='learn-more-rsvp-label'>Date</span>
                <span className='learn-more-rsvp-value'>
                  {informationSession.date}
                </span>
              </div>
              <div className='learn-more-rsvp-item'>
                <span className='learn-more-rsvp-label'>Time</span>
                <span className='learn-more-rsvp-value'>
                  {informationSession.time} Pacific Time
                </span>
              </div>
              {hasRecording ? (
                <div className='learn-more-rsvp-item mb-4'>
                  <span className='learn-more-rsvp-label text-nowrap'>
                    Experience Information <br /> Session Recording
                  </span>
                  <button
                    type='button'
                    className='learn-more-rsvp-value learn-more-session-replay'
                    onClick={() =>
                      openLinkInTab(informationSession.recordingLink, '_blank')
                    }>
                    <PlayIcon
                      useSVGIcon
                      wrapperClasses='d-inline-flex align-items-center'
                      iconClasses='learn-more-play-icon'
                    />
                    Watch Now
                  </button>
                </div>
              ) : (
                <div className='learn-more-rsvp-item mb-4'>
                  <span className='learn-more-rsvp-label'>RSVP Link</span>
                  <a
                    href={informationSession.rsvpLink}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='learn-more-rsvp-value learn-more-rsvp-link'>
                    {informationSession.rsvpLink}
                  </a>
                </div>
              )}
            </div>
            <p className='text-slate-gray my-4 font-paragraph small-paragraph lh-relaxed'>
              {hasRecording
                ? RSVP_RECORDING_SESSION_DESCRIPTION
                : RSVP_SESSION_DESCRIPTION}
            </p>
          </div>
        </div>
      )}
    </section>
  )
}

export default LearnMoreSection
